import {
  type Active,
  type CollisionDetection,
  closestCorners,
  type DroppableContainer,
  pointerWithin,
} from '@dnd-kit/core';
import { hasSortableData } from '@dnd-kit/sortable';

/** Droppable on the Pages menu trigger; hovering it with a tab opens the menu. */
export const PAGES_MENU_TRIGGER_ID = 'menu';
/** Droppable covering the open Pages menu, so its header counts as "in the menu". */
export const PAGES_MENU_SURFACE_ID = 'pages-menu-surface';
/** `SortableContext` ids of the two lists a tab can sit in. */
export const TAB_BAR_LIST_ID = 'tab-bar';
export const PAGES_MENU_LIST_ID = 'pages-menu';

const listOf = (entry: Active | DroppableContainer | undefined) =>
  hasSortableData(entry) ? entry.data.current.sortable.containerId : undefined;

/**
 * The bar and the Pages menu are two sortable lists in one `DndContext`, and
 * the menu opens directly under the bar. Measured against every droppable at
 * once, a bar tab — about the size of the drag ghost — beats the wide menu
 * rows while the pointer is still inside the menu, so a page dragged up the
 * menu jumped into the bar and pushed a bar tab down in its place.
 *
 * So the pointer picks the list first: over the menu it is the menu, over a
 * bar tab it is the bar, and anywhere else the tab stays in the list it is
 * already in. Only then is the nearest slot chosen, from that list alone.
 */
export const tabDragCollisionDetection: CollisionDetection = (args) => {
  const underPointer = pointerWithin(args);

  const trigger = underPointer.filter((c) => c.id === PAGES_MENU_TRIGGER_ID);
  if (trigger.length > 0) return trigger;

  const surface = underPointer.filter((c) => c.id === PAGES_MENU_SURFACE_ID);
  const list = (() => {
    if (surface.length > 0) return PAGES_MENU_LIST_ID;
    const overBarTab = underPointer.some(
      (c) => listOf(c.data?.droppableContainer) === TAB_BAR_LIST_ID
    );
    if (overBarTab) return TAB_BAR_LIST_ID;
    return listOf(args.active) ?? TAB_BAR_LIST_ID;
  })();

  const droppableContainers = args.droppableContainers.filter(
    (container) => listOf(container) === list
  );
  // An empty menu has no rows to land between; the surface itself is the drop.
  if (droppableContainers.length === 0) return surface;
  return closestCorners({ ...args, droppableContainers });
};
